import { forwardRef, useCallback, Ref, KeyboardEvent } from "react"

import { KeyCodes } from "../../../enumerations"

import {
  SelectOptionContainer,
  SelectOptionContent,
  SelectOptionDescription,
  SelectCheckbox,
  FocusRemover,
} from "./styles"
import { SelectOptionAllProps } from "./types"

export const SelectOptionAll = forwardRef(
  (
    {
      id,
      key,
      text,
      isExpanded,
      isMixed,
      description,
      isSelected = false,
      onSelect,
      onOptionItemFocused,
      onPrevious,
      onNext,
      onTab,
      onEscape,
    }: SelectOptionAllProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    const handleKeyDown = useCallback(
      (event: KeyboardEvent) => {
        if (!isExpanded) {
          return
        }
        switch (event.which) {
          case KeyCodes.Up: {
            event.preventDefault()
            onPrevious?.()
            break
          }
          case KeyCodes.Down: {
            event.preventDefault()
            onNext?.()
            break
          }
          case KeyCodes.Enter: {
            event.preventDefault()
            onSelect?.()
            break
          }
          case KeyCodes.Tab:
            onTab?.()
            break
          case KeyCodes.Escape:
            onEscape?.()
            break
          default:
            break
        }
      },
      [isExpanded, onPrevious, onNext, onEscape],
    )

    return (
      <SelectOptionContainer
        id={id}
        tabIndex={key === 0 ? 0 : -1}
        role="option"
        aria-selected={isSelected || isMixed}
        aria-checked={isSelected || isMixed}
        onClick={() => {
          onSelect?.()
        }}
        isSelected={isSelected}
        onKeyDown={handleKeyDown}
      >
        <FocusRemover
          tabIndex={-1}
          ref={ref}
          onFocus={onOptionItemFocused}
          onKeyDown={handleKeyDown}
        >
          <SelectCheckbox
            id={`checkbox-${id}`}
            indeterminate={Boolean(isMixed)}
            checked={isSelected}
          />
          <SelectOptionContent>
            {text}
            {description && (
              <SelectOptionDescription>{description}</SelectOptionDescription>
            )}
          </SelectOptionContent>
        </FocusRemover>
      </SelectOptionContainer>
    )
  },
)
